import { getYear } from 'date-fns'
import React, { Dispatch, SetStateAction, useCallback, useState } from 'react'
import { ISelectOption } from '~/components/shared'
import { IGetVaccines, IGetVaccinesParams, IVaccine } from '~/interfaces'
import { fetchVaccines, message } from '~/services'

interface IVaccinesProviderProps {
  children: React.ReactNode
}

const defaultValue = {
  vaccines: [] as IVaccine[],
  years: [],
  // helpers
  isLoading: false,
  errorVaccines: 'idle'
}

export interface IVaccinesContextHelpers {
  isLoading: boolean
  setIsLoading?: Dispatch<SetStateAction<boolean>>
  errorVaccines: string | boolean
  setErrorVaccines?: Dispatch<SetStateAction<string | boolean>>
}

export interface IVaccinesContextProps extends IVaccinesContextHelpers {
  vaccines: IVaccine[]
  years: ISelectOption[]
  getVaccines: IGetVaccines
}

const VaccinesContext = React.createContext<IVaccinesContextProps>({} as IVaccinesContextProps)

const VaccinesProvider = ({ children }: IVaccinesProviderProps): JSX.Element => {
  const [isLoading, setIsLoading] = useState<boolean>(defaultValue.isLoading)
  const [errorVaccines, setErrorVaccines] = useState<string | boolean>(defaultValue.errorVaccines)
  const [vaccines, setVaccines] = useState<IVaccine[]>(defaultValue.vaccines)
  const [years, setYears] = useState<ISelectOption[]>(defaultValue.years)

  const toTrue = () => true
  const toFalse = () => false

  const reduceYear = useCallback((reducer, vaccines: IVaccine[]) => {
    return new Date(vaccines.reduce(reducer).date).getFullYear()
  }, [])

  const loadYears = useCallback(
    (vaccines: IVaccine[]) => {
      if (vaccines && vaccines.length > 0) {
        const minDate = reduceYear((previous, current) => {
          return Number(getYear(new Date(previous.date))) < Number(getYear(new Date(current.date))) ? previous : current
        }, vaccines)
        const maxDate = reduceYear((previous, current) => {
          return Number(getYear(new Date(previous.date))) > Number(getYear(new Date(current.date))) ? previous : current
        }, vaccines)
        const sequence = Array.from({ length: maxDate - minDate + 1 }, (_, index) => minDate + index)
        const years: ISelectOption[] = sequence.map((i: number) => {
          return {
            value: i,
            label: i.toString()
          } as ISelectOption
        })
        setYears(years)
      }
    },
    [reduceYear]
  )

  const getVaccines: IGetVaccines = useCallback(
    async (params: IGetVaccinesParams) => {
      try {
        setIsLoading(toTrue)
        const vaccines = await fetchVaccines(params)
        setVaccines([...vaccines] as IVaccine[])
        setErrorVaccines(toFalse)
        loadYears(vaccines)
      } catch (err) {
        setErrorVaccines(message('messages.titleError'))
        throw err
      } finally {
        setIsLoading(toFalse)
      }
    },
    [loadYears]
  )

  return (
    <VaccinesContext.Provider
      value={{
        isLoading,
        errorVaccines,
        vaccines,
        years,
        getVaccines
      }}
    >
      {children}
    </VaccinesContext.Provider>
  )
}

const useVaccines = (): IVaccinesContextProps => {
  return React.useContext(VaccinesContext)
}

export { VaccinesContext, VaccinesProvider, useVaccines }
